import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldValues,
  type RegisterOptions,
  type UseFormClearErrors,
  type UseFormRegister,
  type UseFormSetError,
} from 'react-hook-form';
import {
  FieldError,
  FieldLabel,
  campInputClass,
} from '@/features/campaigns/components/FormPrimitives';
import {
  formatMobileDisplay,
  getMobileConfig,
  parseMobileInput,
  type MobileConfig,
} from '@/features/auth/utils/mobileNumber';
import type { TeamFieldValidation, TeamRoleField } from '../api/teamApi';
import {
  AADHAAR_MAX_LENGTH,
  AADHAAR_PATTERN,
  GST_MAX_LENGTH,
  GST_PATTERN,
  PAN_MAX_LENGTH,
  PAN_PATTERN,
  detectKycFieldKind,
  sanitizeKycInput,
  type KycFieldKind,
} from '../utils/kycInputMask';

type DynamicProfileFieldsProps = {
  fields: TeamRoleField[];
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  setError: UseFormSetError<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
  mobileConfig?: MobileConfig;
  /** Field names that should render read-only (e.g. email while editing). */
  readOnlyFields?: string[];
};

function isMobileField(fieldName?: string, fieldType?: string) {
  if (fieldType === 'tel') return true;
  const key = (fieldName || '').replace(/[\s_-]+/g, '').toLowerCase();
  return key === 'mobilenumber' || key === 'phone' || key === 'phonenumber';
}

function canonicalRenderKey(fieldName: string, fieldLabel?: string) {
  const key = fieldName.replace(/[\s_-]+/g, '').toLowerCase();
  const label = (fieldLabel || '').replace(/[\s_-]+/g, '').toLowerCase();
  if (
    key.includes('aadhaar') ||
    key.includes('aadhar') ||
    label.includes('aadhaar') ||
    label.includes('aadhar')
  ) {
    return 'aadharnumber';
  }
  if (key.includes('gst') || label.includes('gst')) return 'gstnumber';
  if (
    (key.includes('pan') || label.includes('pan')) &&
    !key.includes('company') &&
    !label.includes('company')
  ) {
    return 'pannumber';
  }
  return key;
}

function kycLimits(kind: Exclude<KycFieldKind, null>) {
  if (kind === 'pan') {
    return {
      maxLength: PAN_MAX_LENGTH,
      pattern: PAN_PATTERN,
      hint: 'Format: AAAAA9999A',
    };
  }
  if (kind === 'gst') {
    return {
      maxLength: GST_MAX_LENGTH,
      pattern: GST_PATTERN,
      hint: 'Format: 99AAAAA9999A9Z9',
    };
  }
  return {
    maxLength: AADHAAR_MAX_LENGTH,
    pattern: AADHAAR_PATTERN,
    hint: '12-digit Aadhaar number',
  };
}

function buildFieldRules(
  fieldLabel: string,
  fieldType: string | undefined,
  required: boolean | undefined,
  validation?: TeamFieldValidation,
): RegisterOptions {
  const rules: RegisterOptions = {};
  if (required) rules.required = `${fieldLabel} is required`;

  if (fieldType === 'email') {
    rules.pattern = {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Enter a valid email',
    };
  }

  if (!validation) return rules;

  const min = validation.minLength ?? validation.min;
  const max = validation.maxLength ?? validation.max;
  if (min != null) {
    rules.minLength = {
      value: Number(min),
      message: `${fieldLabel} must be at least ${min} characters`,
    };
  }
  if (max != null) {
    rules.maxLength = {
      value: Number(max),
      message: `${fieldLabel} must be at most ${max} characters`,
    };
  }
  if (validation.pattern) {
    try {
      rules.pattern = {
        value: new RegExp(validation.pattern),
        message: `Invalid ${fieldLabel} format`,
      };
    } catch {
      /* ignore bad pattern from API */
    }
  }

  return rules;
}

/**
 * Renders create-fields from `team-members/create-fields`.
 * Dedupes PAN/GST; supports Aadhaar mask and textarea address.
 */
export function DynamicProfileFields({
  fields,
  register,
  control,
  errors,
  setError,
  clearErrors,
  mobileConfig = getMobileConfig(),
  readOnlyFields = [],
}: DynamicProfileFieldsProps) {
  const uniqueFields: TeamRoleField[] = [];
  const seenKeys = new Set<string>();
  for (const item of fields) {
    const rawName = item.fieldId?.fieldName;
    if (!rawName) continue;
    const key = canonicalRenderKey(rawName, item.fieldId?.fieldLabel);
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    uniqueFields.push(item);
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {uniqueFields.map((item) => {
        const field = item.fieldId;
        const name = field?.fieldName;
        if (!name || !item.visible) return null;
        if (field.fieldType === 'file') return null;

        const readOnly = readOnlyFields.includes(name);
        const fieldError = errors[name]?.message as string | undefined;
        const label = field.fieldLabel || name;
        const kycKind = detectKycFieldKind(name, field.fieldType, label);
        const isTextarea =
          field.fieldType === 'textarea' ||
          name.replace(/[\s_-]+/g, '').toLowerCase() === 'address';

        if (isMobileField(name, field.fieldType)) {
          return (
            <div key={name}>
              <FieldLabel required={item.required}>
                {field.fieldLabel || 'Mobile Number'}
              </FieldLabel>
              <div
                className={`flex h-[44px] overflow-hidden rounded-xl border bg-[var(--dash-soft)] ${
                  fieldError ? 'border-red-400' : 'border-[var(--dash-line)]'
                }`}
              >
                <span className="flex h-full shrink-0 items-center gap-1.5 border-r border-[var(--dash-line)] bg-white/70 px-3 text-[12px] font-semibold text-[var(--dash-ink)]">
                  <span aria-hidden>{mobileConfig.flag}</span>
                  <span>{mobileConfig.code}</span>
                </span>
                <Controller
                  name={name}
                  control={control}
                  rules={{
                    required: item.required ? 'Mobile Number is Required' : false,
                    pattern: {
                      value: mobileConfig.regex,
                      message: mobileConfig.message,
                    },
                  }}
                  render={({ field: rhf }) => (
                    <input
                      type="tel"
                      inputMode="numeric"
                      placeholder={mobileConfig.placeholder}
                      readOnly={readOnly}
                      className="h-full min-w-0 flex-1 border-none bg-transparent px-3 text-sm font-medium text-[var(--dash-ink)] outline-none placeholder:text-[var(--dash-muted)]"
                      value={formatMobileDisplay(
                        String(rhf.value || ''),
                        mobileConfig,
                      )}
                      onChange={(e) => {
                        const { value, rejected } = parseMobileInput(
                          e.target.value,
                          mobileConfig,
                        );
                        rhf.onChange(value);
                        if (rejected) {
                          setError(name, {
                            type: 'pattern',
                            message: mobileConfig.message,
                          });
                        } else {
                          clearErrors(name);
                        }
                      }}
                      onBlur={rhf.onBlur}
                    />
                  )}
                />
              </div>
              <FieldError message={fieldError} />
            </div>
          );
        }

        if (kycKind) {
          const { maxLength, pattern, hint } = kycLimits(kycKind);
          const apiPattern = field.validation?.pattern;
          const finalPattern = apiPattern
            ? (() => {
                try {
                  return new RegExp(apiPattern);
                } catch {
                  return pattern;
                }
              })()
            : pattern;

          return (
            <div key={name}>
              <FieldLabel required={item.required}>{label}</FieldLabel>
              <Controller
                name={name}
                control={control}
                defaultValue=""
                rules={{
                  validate: (raw) => {
                    const value = String(raw ?? '').trim();
                    if (!value) {
                      return item.required ? `${label} is required` : true;
                    }
                    if (value.length < maxLength) {
                      return `${label} must be ${maxLength} characters`;
                    }
                    if (!finalPattern.test(value)) {
                      return `Invalid ${label} format`;
                    }
                    return true;
                  },
                }}
                render={({ field: rhf }) => (
                  <input
                    type="text"
                    inputMode={kycKind === 'aadhaar' ? 'numeric' : 'text'}
                    autoCapitalize={kycKind === 'aadhaar' ? 'off' : 'characters'}
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={maxLength}
                    placeholder={field.placeholder || hint}
                    readOnly={readOnly}
                    className={`${campInputClass} ${readOnly ? 'cursor-not-allowed opacity-70' : ''} ${
                      kycKind === 'aadhaar' ? '' : 'uppercase'
                    }`}
                    aria-invalid={Boolean(fieldError)}
                    value={String(rhf.value ?? '')}
                    onChange={(e) => {
                      rhf.onChange(sanitizeKycInput(kycKind, e.target.value));
                    }}
                    onBlur={rhf.onBlur}
                  />
                )}
              />
              <FieldError message={fieldError} />
            </div>
          );
        }

        if (isTextarea) {
          return (
            <div key={name} className="sm:col-span-2">
              <FieldLabel required={item.required}>{label}</FieldLabel>
              <textarea
                rows={3}
                placeholder={
                  field.placeholder || `Enter ${label.toLowerCase()}`
                }
                readOnly={readOnly}
                className={`${campInputClass} min-h-[96px] resize-y py-2.5 ${
                  readOnly ? 'cursor-not-allowed opacity-70' : ''
                }`}
                aria-invalid={Boolean(fieldError)}
                {...register(
                  name,
                  buildFieldRules(
                    label,
                    field.fieldType,
                    item.required,
                    field.validation,
                  ),
                )}
              />
              <FieldError message={fieldError} />
            </div>
          );
        }

        const inputType =
          field.fieldType === 'email'
            ? 'email'
            : field.fieldType === 'date'
              ? 'date'
              : 'text';

        return (
          <div key={name}>
            <FieldLabel required={item.required}>{label}</FieldLabel>
            <input
              type={inputType}
              placeholder={
                field.placeholder || `Enter ${label.toLowerCase()}`
              }
              readOnly={readOnly}
              className={`${campInputClass} ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}
              aria-invalid={Boolean(fieldError)}
              {...register(
                name,
                buildFieldRules(
                  label,
                  field.fieldType,
                  item.required,
                  field.validation,
                ),
              )}
            />
            <FieldError message={fieldError} />
          </div>
        );
      })}
    </div>
  );
}
